import { writable } from 'svelte/store';
import { trackEvent } from '$lib/services/analytics';
import { StorageKey } from '$lib/constants/storageKeys';

export type UserRole = "admin" | "user" | "teacher" | "student";

interface User {
	id: string;
	email: string;
	role: UserRole;
}

interface UserState {
	user: User | null;
	token: string | null;
	isAuthenticated: boolean;
	initialised: boolean;
}

function decodeTokenPayload(token: string): User | null {
	try {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		const payload = JSON.parse(atob(parts[1]));
		if (!payload.sub) return null;
		return {
			id: payload.sub,
			email: payload.email ?? '',
			role: (payload.role as UserRole) ?? 'user',
		};
	} catch {
		return null;
	}
}

function createUserStore() {
	const { subscribe, set, update } = writable<UserState>({
		user: null,
		token: null,
		isAuthenticated: false,
		initialised: false
	});

	return {
		subscribe,
		login: (user: Omit<User, "role"> & { role?: string }, token: string) => {
			localStorage.setItem(StorageKey.AuthToken, token);
			set({
				user: { ...user, role: (user.role as UserRole) ?? "user" },
				token,
				isAuthenticated: true,
				initialised: true,
			});
		},
		logout: () => {
			trackEvent('user_logged_out');
			localStorage.removeItem(StorageKey.AuthToken);
			set({ user: null, token: null, isAuthenticated: false, initialised: true });
			// Clear HttpOnly refresh token cookie server-side (fire & forget)
			import('$lib/services/api').then(({ api }) => api.auth.logout()).catch(() => {});
		},
		init: () => {
			const token = localStorage.getItem(StorageKey.AuthToken);
			if (token) {
				// Decode JWT payload to hydrate user (sub, role) without an API call
				const user = decodeTokenPayload(token);
				update(state => ({ ...state, token, user, isAuthenticated: true, initialised: true }));
			} else {
				update(state => ({ ...state, initialised: true }));
			}
		}
	};
}

export const userStore = createUserStore();
